import { useState, useEffect, useMemo, useRef } from "react";
import useIsMobile from "../client/hooks/useIsMobile";
import { startOfMonth } from "date-fns";
import {
  deleteTrades,
  getTradesByUser,
  getTradeMetrics,
  getAccountsByUser,
  getAccountGroupsByUser,
  getTradingStrategiesByUser,
  getAllTradesForAnalytics,
  getTradingSessionsByUser,
  getTradeCustomFieldDefinitions,
  getTradeColumnPreferences,
  getTradeTagSuggestions,
  getTradeTagsByUser,
  getActiveLayout,
  ensureJournalDefaultLayout,
  resetLayoutContent,
  useQuery,
  useAction,
} from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { PaymentPlanId } from "../payment/plans";
import { useSearchParams } from "react-router-dom";
import { Layers, ChevronDown, Loader2 } from "lucide-react";
import { Button } from "../client/components/ui/button";
import PlanSelectionModal from "../dashboard/PlanSelectionModal";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import { toast } from "../client/hooks/use-toast";
import { useToast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";
import { ColumnConfigDialog } from "./ColumnConfigDialog";
import { type AdvancedFilters } from "./AdvancedFiltersDialog";
import { type AccountFilterValue } from "../trading-accounts/AccountGroupFilter";
import AddTradeModal from "./AddTradeModal";
import ManualTradeDialog from "./ManualTradeDialog";
import CsvImportDialog from "./CsvImportDialog";
import { useTradeEditing } from "./useTradeEditing";
import { motion } from "framer-motion";
import { PageTransition } from "../client/components/ui/motion";
import { type DateRange } from "../client/components/DateRangePicker";
import { presetToDateRange } from "../client/utils/dateRange";
import { calculateMetrics, netPnl } from "./analytics";
import { filterTradesForWidgets } from "./journalWidgetFilters";
import { useIsDark, useThemeId } from "../client/components/ui/card-section-header";
import { WidgetDataProvider, type DashboardData } from "../dashboard/widgets/WidgetDataContext";
import DashboardLayout from "../dashboard/widgets/DashboardLayout";
import EditModeBar from "../dashboard/widgets/EditModeBar";
import AddWidgetPicker, { TOPBAR_WIDGET_KEYS } from "../dashboard/widgets/AddWidgetPicker";
import ResetLayoutDialog from "../dashboard/widgets/ResetLayoutDialog";
import LayoutLibraryModal from "../dashboard/widgets/LayoutLibraryModal";
import { SortableTopbarSlot, TopbarDndWrapper } from "../dashboard/widgets/TopbarSlotRenderer";
import TopbarFilterBar from "../dashboard/widgets/TopbarFilterBar";
import { useActiveLayout, compactSlots } from "../dashboard/widgets/useActiveLayout";
import { useLayoutsLibrary } from "../dashboard/widgets/useLayoutsLibrary";
import {
  getDefaultJournalLayout,
  withDefaultLayoutFallback,
  ANALYTICS_WIDGET_KEYS,
  metricsForWidgetKeys,
} from "../dashboard/widgets/registry";
import { useAnalyticsData } from "../dashboard/widgets/useAnalyticsData";
import type { LayoutSnapshot, SlotPlacement, WidgetKey } from "../dashboard/widgets/types";
import { trackBulkTradeDeleted, trackWidgetAdded, trackWidgetRemoved, trackLayoutSaved } from "../client/utils/posthog";
import EdgyHeaderButton from "../edgy/EdgyHeaderButton";

// TradeFormData and initialTradeFormData are now imported from "./TradeForm"

const emptyAdvancedFilters: AdvancedFilters = {
  accountId: "",
  groupId: "",
  strategyIds: [],
  status: "all",
  tagIds: [],
  minPnl: "",
  maxPnl: "",
  customFields: {},
};

/**
 * Widgets du journal qui ont besoin des trades / sessions sans passer par une
 * métrique analytics. Ils sont invisibles pour `ANALYTICS_WIDGET_KEYS`, qui est
 * dérivé de WIDGET_ANALYTICS_METRICS.
 */
const JOURNAL_TRADE_HUNGRY_WIDGET_KEYS: WidgetKey[] = ["sessions-list", "last-session"];

export default function TradingJournalPage() {
  const t = useTranslation();
  const isDark = useIsDark();
  const themeId = useThemeId();
  const { toast: toastFn } = useToast();
  const { data: user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const isFreePlan = !user?.subscriptionPlan || user.subscriptionPlan === PaymentPlanId.Free;

  // — Dialog / modal state (preserved from previous page) —
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isColumnConfigDialogOpen, setIsColumnConfigDialogOpen] = useState(false);
  const [bulkDeleteIds, setBulkDeleteIds] = useState<string[]>([]);
  const [isBulkDeleteConfirmOpen, setIsBulkDeleteConfirmOpen] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);

  // — Journal filter state —
  const [journalSymbol, setJournalSymbol] = useState("");
  const [journalSide, setJournalSide] = useState<"all" | "long" | "short">("all");
  const [dateRange, setDateRange] = useState<DateRange>(() => presetToDateRange("last_30_days"));
  const [accountFilter, setAccountFilter] = useState<AccountFilterValue>({ type: "all" });
  const [strategyIds, setStrategyIds] = useState<string[]>([]);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>(emptyAdvancedFilters);

  // — Actions —
  const deleteTradesFn = useAction(deleteTrades);

  // — Queries (preserved) —
  const { data: accountsData } = useQuery(getAccountsByUser);
  const accounts = (accountsData as any)?.accounts ?? [];
  const accountsList = Array.isArray(accounts) ? accounts : [];
  const hiddenAccountsCount: number = (accountsData as any)?.hiddenAccountsCount ?? 0;
  const { data: accountGroups = [] } = useQuery(getAccountGroupsByUser);

  const { data: strategies = [] } = useQuery(getTradingStrategiesByUser);
  const { data: customFields = [] } = useQuery(getTradeCustomFieldDefinitions);
  const { data: columnPreferences = [], refetch: refetchColumnPreferences } = useQuery(getTradeColumnPreferences);
  const { data: tags = [] } = useQuery(getTradeTagsByUser);
  const { data: tagSuggestions = [] } = useQuery(getTradeTagSuggestions, { fieldId: null });

  // Lightweight count-only query: we only need `total` here (used for the Free-plan
  // limit warning). The trade rows are fetched by the table widget itself, and the
  // headline KPIs come from getTradeMetrics below — so we no longer pull a capped
  // page of trades on this page.
  const { data: metricsTradesData, refetch: refetchTradeCount } = useQuery(getTradesByUser, {
    skip: 0,
    take: 1,
    ...(journalSymbol && { symbol: journalSymbol }),
    ...(journalSide !== "all" && { type: journalSide }),
    startDate: dateRange.from ?? undefined,
    endDate: dateRange.to ?? undefined,
    ...(accountFilter.type === "account" && accountFilter.id
      ? { accountId: accountFilter.id }
      : accountFilter.type === "group" && accountFilter.id
      ? { groupId: accountFilter.id }
      : accountFilter.type === "none"
      ? { unassignedOnly: true }
      : {}),
    ...(strategyIds.length > 0 && { strategyIds }),
    ...(advancedFilters.status !== "all" && { status: advancedFilters.status }),
    ...(advancedFilters.tagIds.length > 0 && { tagIds: advancedFilters.tagIds }),
    ...(advancedFilters.minPnl && { minPnl: parseFloat(advancedFilters.minPnl) }),
    ...(advancedFilters.maxPnl && { maxPnl: parseFloat(advancedFilters.maxPnl) }),
  });

  // Headline KPIs are computed server-side over the FULL filtered set (no 1000 cap),
  // so net-pnl / win-rate / profit-factor / avg-win-loss stay accurate for large journals.
  const { data: metricsData, refetch: refetchTradeMetrics } = useQuery(getTradeMetrics, {
    ...(journalSymbol && { symbol: journalSymbol }),
    ...(journalSide !== "all" && { type: journalSide }),
    startDate: dateRange.from ?? undefined,
    endDate: dateRange.to ?? undefined,
    ...(accountFilter.type === "account" && accountFilter.id
      ? { accountId: accountFilter.id }
      : accountFilter.type === "group" && accountFilter.id
      ? { groupId: accountFilter.id }
      : accountFilter.type === "none"
      ? { unassignedOnly: true }
      : {}),
    ...(strategyIds.length > 0 && { strategyIds }),
    ...(advancedFilters.status !== "all" && { status: advancedFilters.status }),
    ...(advancedFilters.tagIds.length > 0 && { tagIds: advancedFilters.tagIds }),
    ...(advancedFilters.minPnl && { minPnl: parseFloat(advancedFilters.minPnl) }),
    ...(advancedFilters.maxPnl && { maxPnl: parseFloat(advancedFilters.maxPnl) }),
  });
  const metrics = (metricsData as any) ?? calculateMetrics([]);

  // Refresh both the trade-count (Free-plan limit) and the server-side KPIs.
  const refetchMetricsTrades = () => {
    refetchTradeCount();
    refetchTradeMetrics();
  };

  // Journal status / pnl / tags are derived from advancedFilters so both the
  // topbar widgets and the advanced filter dialog stay in sync.
  const journalStatus = advancedFilters.status;
  const journalMinPnl = advancedFilters.minPnl;
  const journalMaxPnl = advancedFilters.maxPnl;
  const journalTagIds = advancedFilters.tagIds;

  // — URL params: open create / import dialogs —
  useEffect(() => {
    if (searchParams.get("create") === "true") {
      setIsAddModalOpen(true);
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("create");
        return newParams;
      });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get("import") === "csv") {
      setIsImportDialogOpen(true);
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("import");
        return newParams;
      });
    }
  }, [searchParams, setSearchParams]);

  // — Trade mutations (preserved) —
  // Modifier / supprimer un trade : module partagé avec le Lite (useTradeEditing).
  const tradeEditing = useTradeEditing({ onChanged: () => refetchMetricsTrades() });

  const confirmBulkDelete = async () => {
    if (bulkDeleteIds.length === 0) return;
    setIsBulkDeleteConfirmOpen(false);
    try {
      await deleteTradesFn({ ids: bulkDeleteIds });
      trackBulkTradeDeleted(bulkDeleteIds.length);
      toast({
        title: t.journal.bulkDeleteSuccess.replace("{count}", String(bulkDeleteIds.length)),
      });
      setBulkDeleteIds([]);
      refetchMetricsTrades();
    } catch (error: any) {
      toast({
        title: t.common.error,
        description: error.message || t.journal.bulkDeleteError,
        variant: "destructive",
      });
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Layout system (scope "journal") — mirrors DashboardView
  // ─────────────────────────────────────────────────────────────────────────
  const { data: serverLayout, refetch: refetchLayout } = useQuery(getActiveLayout, { scope: "journal" });

  const ensureJournalFn = useAction(ensureJournalDefaultLayout);
  const ensureCalledRef = useRef(false);
  useEffect(() => {
    if (ensureCalledRef.current) return;
    ensureCalledRef.current = true;
    ensureJournalFn(undefined).then(() => refetchLayout()).catch(() => {});
  }, []);

  const initialLayout: LayoutSnapshot = useMemo(() => {
    if (!serverLayout) return getDefaultJournalLayout();
    const rows = [...serverLayout.rows].sort((a, b) => a.order - b.order);
    const allSlots: SlotPlacement[] = [];

    const isNewStyle = rows.length === 0 || rows.every((r) => r.cols === 6);

    if (isNewStyle) {
      for (const row of rows) {
        for (const s of row.slots) {
          allSlots.push({
            id: s.id,
            widgetKey: s.widgetKey as WidgetKey,
            colStart: s.colStart,
            rowStart: row.order,
            colSpan: s.colSpan,
            rowSpan: s.rowSpan,
            ...(s.notebookNoteId ? { notebookNoteId: s.notebookNoteId } : {}),
            ...((s as any).placement ? { placement: (s as any).placement } : {}),
            ...((s as any).defaultFilterValue != null ? { defaultFilterValue: (s as any).defaultFilterValue } : {}),
            ...((s as any).viewMode != null ? { viewMode: (s as any).viewMode } : {}),
          });
        }
      }
    } else {
      let cursor = 1;
      for (const row of rows) {
        const maxRowSpan = row.slots.reduce((m: number, s: any) => Math.max(m, s.rowSpan ?? 1), 1);
        for (const s of row.slots) {
          allSlots.push({
            id: s.id,
            widgetKey: s.widgetKey as WidgetKey,
            colStart: s.colStart,
            rowStart: cursor,
            colSpan: s.colSpan,
            rowSpan: s.rowSpan,
            ...(s.notebookNoteId ? { notebookNoteId: s.notebookNoteId } : {}),
          });
        }
        cursor += maxRowSpan;
      }
    }
    const topbar = allSlots.filter((s) => s.placement === "topbar");
    const grid = allSlots.filter((s) => s.placement !== "topbar");
    return withDefaultLayoutFallback({ slots: [...topbar, ...compactSlots(grid)] }, "journal");
  }, [serverLayout]);

  const {
    layout,
    addWidget,
    removeWidget,
    setWidgetSize,
    moveWidget,
    swapWidgets,
    flushSave,
    compactAndFlushSave,
    updateSlotDefaultFilterValue,
    updateSlotViewMode,
  } = useActiveLayout(initialLayout, (serverLayout as any)?.id ?? null, undefined, false);

  const topbarSlots = useMemo(
    () => layout.slots.filter((s) => s.placement === "topbar").sort((a, b) => a.colStart - b.colStart),
    [layout.slots],
  );

  // — Edit-mode state —
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [layoutLibraryOpen, setLayoutLibraryOpen] = useState(false);
  const isDuplicatingRef = useRef(false);
  const { activeLayout: libraryActiveLayout, duplicate, activate } = useLayoutsLibrary("journal");
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [pickerContext, setPickerContext] = useState<{
    colStart: number;
    rowStart: number;
    maxColSpan: number;
    context?: "topbar" | "grid";
  } | null>(null);
  const resetLayoutFn = useAction(resetLayoutContent);
  const isMobile = useIsMobile();

  // ─────────────────────────────────────────────────────────────────────────
  // Données des widgets analytics / sessions — chargement à la carte
  // ─────────────────────────────────────────────────────────────────────────
  // Le sélecteur de widgets laisse l'utilisateur poser ici n'importe quel widget
  // analytics ou session. Ces widgets lisent `trades`, `sessions` et
  // `analyticsData` : sans les fournir, ils s'affichaient tous vides.
  //
  // Cette page a délibérément CESSÉ de charger les trades (getTradesByUser est
  // appelé avec take: 1, la table les récupère elle-même). On ne les recharge
  // donc QUE si un widget du layout en a réellement besoin — même motif que
  // DashboardView : aucun coût pour un journal qui n'affiche que sa table.
  //
  // ⚠️ `sessions-list` et `last-session` ne déclarent AUCUNE métrique analytics
  // (ils lisent `sessions` et `statsTradesList` directement), donc
  // ANALYTICS_WIDGET_KEYS ne les couvre pas. Posés seuls, ils resteraient vides
  // si on s'en tenait à cette liste — d'où l'union explicite ci-dessous.
  const hasWidgetsNeedingTrades = useMemo(
    () =>
      layout.slots.some(
        (s) =>
          ANALYTICS_WIDGET_KEYS.includes(s.widgetKey) ||
          JOURNAL_TRADE_HUNGRY_WIDGET_KEYS.includes(s.widgetKey),
      ),
    [layout.slots],
  );

  // Les mêmes filtres que la table et les KPI : un widget « Long vs Short » posé
  // à côté de la table doit parler de la même période et du même compte.
  const analyticsQueryArgs = useMemo(() => {
    const base =
      accountFilter.type === "none"
        ? { unassignedOnly: true }
        : accountFilter.type === "account"
        ? { accountId: accountFilter.id }
        : accountFilter.type === "group"
        ? { groupId: accountFilter.id }
        : {};
    return {
      ...base,
      // La table du journal n'exclut PAS les comptes désactivés : les widgets
      // posés à côté d'elle doivent porter sur le même ensemble de trades,
      // sans quoi deux totaux différents s'afficheraient sur le même écran.
      excludeInactive: false,
      startDate: dateRange.from ?? undefined,
      endDate: dateRange.to ?? undefined,
      ...(strategyIds.length > 0 && { strategyIds }),
    };
  }, [accountFilter, dateRange, strategyIds]);

  const { data: analyticsTrades } = useQuery(getAllTradesForAnalytics, analyticsQueryArgs, {
    enabled: hasWidgetsNeedingTrades,
  });
  const { data: journalSessions } = useQuery(getTradingSessionsByUser, undefined, {
    enabled: hasWidgetsNeedingTrades,
  });

  // `getAllTradesForAnalytics` applique compte / dates / stratégies côté serveur.
  // Les filtres restants du journal (symbole, sens, statut, tags, min/max PnL) ne
  // font pas partie de son schéma : on les rejoue sur le résultat, avec la même
  // sémantique que le serveur. Sans cela, filtrer la table sur un symbole
  // laisserait les widgets parler de tout le portefeuille.
  const widgetTrades = useMemo(
    () =>
      filterTradesForWidgets((analyticsTrades ?? []) as any[], {
        symbol: journalSymbol,
        side: journalSide,
        status: journalStatus,
        tagIds: journalTagIds,
        minPnl: journalMinPnl,
        maxPnl: journalMaxPnl,
      }),
    [
      analyticsTrades,
      journalSymbol,
      journalSide,
      journalStatus,
      journalTagIds,
      journalMinPnl,
      journalMaxPnl,
    ],
  );

  const widgetSessions = useMemo(() => (journalSessions ?? []) as any[], [journalSessions]);

  const analyticsMetrics = useMemo(
    () => metricsForWidgetKeys(layout.slots.map((s) => s.widgetKey)),
    [layout.slots],
  );
  const analyticsData = useAnalyticsData(
    widgetTrades,
    widgetSessions,
    hasWidgetsNeedingTrades && analyticsMetrics.size > 0,
    analyticsMetrics,
  );

  // Courbe d'équity — même construction que DashboardView, sur les trades filtrés.
  const equityCurveData = useMemo(() => {
    const activeAccounts =
      accountFilter.type === "none"
        ? []
        : accountFilter.type === "account"
        ? accountsList.filter((a: any) => a.id === accountFilter.id)
        : accountFilter.type === "group"
        ? accountsList.filter((a: any) => a.groupId === accountFilter.id)
        : accountsList;

    const initialBalance =
      activeAccounts.reduce((sum: number, a: any) => {
        if (a.initialBalance && a.initialBalance > 0) return sum + a.initialBalance;
        const tierBase = a.activeTier?.accountSize ?? a.activeTier?.minBalance ?? 0;
        return tierBase > 0 ? sum + tierBase : sum;
      }, 0) || null;

    const closedTrades = widgetTrades
      .filter((tr: any) => tr.pnl !== null && tr.pnl !== undefined)
      .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());

    if (closedTrades.length === 0) {
      return { points: [] as { x: number; y: number }[], pctChange: 0, totalPnl: 0, initialBalance, xMin: 0, xMax: 0 };
    }

    const startValue = initialBalance ?? 0;
    let running = startValue;
    const points: { x: number; y: number }[] =
      startValue !== 0 ? [{ x: new Date(closedTrades[0].date).getTime() - 1, y: startValue }] : [];
    for (const tr of closedTrades) {
      running += netPnl(tr);
      points.push({ x: new Date(tr.date).getTime(), y: parseFloat(running.toFixed(2)) });
    }
    const totalPnl = running - startValue;
    const pctChange = initialBalance && initialBalance !== 0 ? (totalPnl / initialBalance) * 100 : 0;
    return {
      points,
      pctChange,
      initialBalance,
      totalPnl,
      xMin: points[0].x,
      xMax: points[points.length - 1].x,
    };
  }, [widgetTrades, accountsList, accountFilter]);

  // — Build the widget context —
  const journalData: DashboardData = {
    trades: widgetTrades,
    routines: [],
    goals: [],
    accounts: accountsList as any[],
    groups: (accountGroups || []) as any[],
    strategies: (strategies || []) as any[],
    sessions: widgetSessions,
    metrics,
    statsTrades: widgetTrades,
    statsTradesList: widgetTrades,
    equityCurveData: equityCurveData as any,
    accountEvents: [],
    fundedEvents: [],
    payoutEvents: [],
    violationsByDate: new Map(),
    pendingRoutines: [],
    topGoals: [],
    statsPeriod: "30d" as const,
    accountFilter,
    setAccountFilter,
    strategyIds,
    setStrategyIds,
    statsStartDate: dateRange.from ?? null,
    statsEndDate: dateRange.to ?? null,
    setSelectedDay: () => {},
    handleCompleteRoutine: () => {},
    setSelectedMonth: () => {},
    onAddTrade: () => setIsAddModalOpen(true),
    onPromoteToFunded: () => {},
    onOpenPlanModal: () => setShowPlanModal(true),
    isDark,
    themeId,
    isFreePlan,
    readOnly: false,
    hasMentor: false,
    t,
    chartMode: "networth",
    setChartMode: () => {},
    analyticsData,
    charges: [],
    tptWalletContext: undefined,
    filteredAccounts: accountsList as any[],
    calendarEvents: [],
    pfmTemplateFilter: null,
    setPfmTemplateFilter: () => {},
    accountStatusFilter: null,
    setAccountStatusFilter: () => {},
    dateRange,
    setDateRange,
    calendarMonth: startOfMonth(new Date()),
    setCalendarMonth: () => {},
    showImportModal: false,
    setShowImportModal: () => {},
    showAddPayoutModal: false,
    setShowAddPayoutModal: () => {},
    isLoadingPfm: false,
    userId: user?.id,
    hiddenAccountsCount,
    showPlanModal,
    setShowPlanModal,
    // — Journal-specific —
    journalSymbol,
    setJournalSymbol,
    journalSide,
    setJournalSide,
    journalStatus,
    setJournalStatus: (v) => setAdvancedFilters((p) => ({ ...p, status: v })),
    journalMinPnl,
    journalMaxPnl,
    setJournalMinPnl: (v) => setAdvancedFilters((p) => ({ ...p, minPnl: v })),
    setJournalMaxPnl: (v) => setAdvancedFilters((p) => ({ ...p, maxPnl: v })),
    journalTagIds,
    setJournalTagIds: (ids) => setAdvancedFilters((p) => ({ ...p, tagIds: ids })),
    journalTags: tags as any[],
    journalAdvancedFilters: advancedFilters,
    setJournalAdvancedFilters: (f) => {
      setAdvancedFilters(f);
      setStrategyIds(f.strategyIds ?? []);
    },
    journalCustomFields: customFields as any[],
    journalColumnPreferences: columnPreferences as any[],
    journalTagSuggestions: tagSuggestions as any[],
    onEditTrade: (trade) => tradeEditing.openEdit(trade),
    onDeleteTrade: (trade) => tradeEditing.openDelete(trade),
    onBulkDeleteTrades: (ids) => {
      setBulkDeleteIds(ids);
      setIsBulkDeleteConfirmOpen(true);
    },
    onColumnsChanged: () => { refetchColumnPreferences(); },
    onJournalTradesChanged: () => { refetchMetricsTrades(); },
  };

  const extraTopbarActions = (
    <>
      {/* Edgy sits with the page's other toolbar actions. */}
      <EdgyHeaderButton />
      <Button
        variant="outline"
        size="sm"
        className="text-xs h-8 gap-1 max-w-[140px]"
        onClick={() => setLayoutLibraryOpen(true)}
      >
        <Layers className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{(libraryActiveLayout as any)?.name ?? t.dashboard.layouts.menuTitle}</span>
        <ChevronDown className="h-3 w-3 shrink-0" />
      </Button>
      {!isEditMode && (
        <Button
          size="sm"
          variant="outline"
          className="text-xs h-8 min-w-[5rem]"
          disabled={isDuplicating}
          onClick={async () => {
            if (isFreePlan) {
              setShowPlanModal(true);
              return;
            }
            if ((libraryActiveLayout as any)?.isReadOnly) {
              if (isDuplicatingRef.current) return;
              isDuplicatingRef.current = true;
              setIsDuplicating(true);
              let success = false;
              try {
                const created = await duplicate((libraryActiveLayout as any).id, t.dashboard.layouts.newLayout);
                if (created) {
                  const activated = await activate((created as any).id);
                  if (activated) success = true;
                }
              } finally {
                setIsDuplicating(false);
                isDuplicatingRef.current = false;
              }
              if (!success) return;
              await refetchLayout();
            }
            setIsEditMode(true);
          }}
        >
          {isDuplicating ? (
            <><Loader2 className="h-3 w-3 animate-spin" />{t.dashboard.customize}</>
          ) : (
            t.dashboard.customize
          )}
        </Button>
      )}
    </>
  );

  return (
    <PageTransition className="w-full">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="sticky top-0 z-30 -mx-3 sm:-mx-5 mb-5 flex items-center justify-between gap-4 min-h-10 border-b border-border bg-background/80 px-3 sm:px-5 py-3 backdrop-blur-md"
      >
        <h1 className="flex items-center gap-2.5 text-lg font-semibold shrink-0">
          {t.journal.title}
        </h1>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {isEditMode ? (
            <TopbarDndWrapper
              slots={topbarSlots}
              onSwap={(idA, idB) => { swapWidgets(idA, idB); flushSave(); }}
            >
              {topbarSlots.map((slot) => (
                <SortableTopbarSlot
                  key={slot.id}
                  id={slot.id}
                  slot={slot}
                  isEditMode={true}
                  dateRange={dateRange}
                  accountFilter={accountFilter}
                  strategyIds={strategyIds}
                  accounts={accountsList as any[]}
                  groups={(accountGroups || []) as any[]}
                  strategies={(strategies || []) as any[]}
                  onDateRangeChange={setDateRange}
                  onAccountFilterChange={setAccountFilter}
                  onStrategyIdsChange={setStrategyIds}
                  onAddTrade={() => setIsAddModalOpen(true)}
                  onAddPayout={() => {}}
                  onRemove={() => { removeWidget(slot.id); trackWidgetRemoved(slot.widgetKey); flushSave(); }}
                  onPin={(value) => {
                    updateSlotDefaultFilterValue(slot.id, value);
                    flushSave();
                    toastFn({ description: value === null ? t.pages.unpinDefault : t.pages.pinAsDefaultConfirm, duration: 1500 });
                  }}
                  journalSymbol={journalSymbol}
                  onJournalSymbolChange={setJournalSymbol}
                  journalSide={journalSide}
                  onJournalSideChange={setJournalSide}
                  journalStatus={journalStatus}
                  onJournalStatusChange={(v) => setAdvancedFilters((p) => ({ ...p, status: v }))}
                  journalMinPnl={journalMinPnl}
                  journalMaxPnl={journalMaxPnl}
                  onJournalMinPnlChange={(v) => setAdvancedFilters((p) => ({ ...p, minPnl: v }))}
                  onJournalMaxPnlChange={(v) => setAdvancedFilters((p) => ({ ...p, maxPnl: v }))}
                  journalTags={tags as any[]}
                  journalTagIds={journalTagIds}
                  onJournalTagIdsChange={(ids) => setAdvancedFilters((p) => ({ ...p, tagIds: ids }))}
                />
              ))}
            </TopbarDndWrapper>
          ) : (
            <TopbarFilterBar
              slots={topbarSlots}
              dateRange={dateRange}
              accountFilter={accountFilter}
              strategyIds={strategyIds}
              accounts={accountsList as any[]}
              groups={(accountGroups || []) as any[]}
              strategies={(strategies || []) as any[]}
              onDateRangeChange={setDateRange}
              onAccountFilterChange={setAccountFilter}
              onStrategyIdsChange={setStrategyIds}
              onAddTrade={() => setIsAddModalOpen(true)}
              onAddPayout={() => {}}
              onRemove={() => {}}
              onPin={undefined}
              journalSymbol={journalSymbol}
              onJournalSymbolChange={setJournalSymbol}
              journalSide={journalSide}
              onJournalSideChange={setJournalSide}
              journalStatus={journalStatus}
              onJournalStatusChange={(v) => setAdvancedFilters((p) => ({ ...p, status: v }))}
              journalMinPnl={journalMinPnl}
              journalMaxPnl={journalMaxPnl}
              onJournalMinPnlChange={(v) => setAdvancedFilters((p) => ({ ...p, minPnl: v }))}
              onJournalMaxPnlChange={(v) => setAdvancedFilters((p) => ({ ...p, maxPnl: v }))}
              journalTags={tags as any[]}
              journalTagIds={journalTagIds}
              onJournalTagIdsChange={(ids) => setAdvancedFilters((p) => ({ ...p, tagIds: ids }))}
              extraActions={extraTopbarActions}
            />
          )}
          {isEditMode && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-8"
              onClick={() => setPickerContext({ colStart: 1, rowStart: 1, maxColSpan: 6, context: "topbar" })}
            >
              {t.pages.topbarAdd}
            </Button>
          )}
          <LayoutLibraryModal
            open={layoutLibraryOpen}
            onOpenChange={setLayoutLibraryOpen}
            scope="journal"
            isFreePlan={isFreePlan}
            onUpgradeClick={() => setShowPlanModal(true)}
            activeLayoutId={(libraryActiveLayout as any)?.id ?? null}
            onNewLayoutActivated={async () => {
              await refetchLayout();
              setIsEditMode(true);
            }}
            onBeforeActivate={async () => {
              await flushSave();
            }}
          />
        </div>
      </motion.div>

      {hiddenAccountsCount > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-4 py-3 text-sm text-yellow-300">
          <span>
            {t.tradingAccounts.hiddenBanner.title.replace("{{count}}", String(hiddenAccountsCount))}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="ml-4 shrink-0 border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/20"
            onClick={() => setShowPlanModal(true)}
          >
            {t.tradingAccounts.hiddenBanner.cta}
          </Button>
        </div>
      )}

      {isEditMode && (
        <EditModeBar
          onReset={() => setShowResetDialog(true)}
          onDone={async () => { await compactAndFlushSave(); trackLayoutSaved(); setIsEditMode(false); }}
        />
      )}

      <WidgetDataProvider value={journalData}>
        <DashboardLayout
          layout={layout}
          isEditMode={isEditMode}
          isMobile={isMobile}
          onAddWidgetClick={(colStart, rowStart, maxColSpan) =>
            setPickerContext({ colStart, rowStart, maxColSpan })
          }
          onRemoveWidget={(slotId) => {
            const slot = layout.slots.find((s) => s.id === slotId);
            if (slot) trackWidgetRemoved(slot.widgetKey);
            removeWidget(slotId);
          }}
          onWidgetSizeChange={setWidgetSize}
          onMoveWidget={moveWidget}
          onSwapWidgets={swapWidgets}
          onUpdateSlotConfig={updateSlotDefaultFilterValue}
          onUpdateSlotViewMode={updateSlotViewMode}
        />
      </WidgetDataProvider>

      <AddWidgetPicker
        open={pickerContext !== null}
        onOpenChange={(open) => { if (!open) setPickerContext(null); }}
        layout={layout}
        maxColSpan={pickerContext?.maxColSpan ?? 1}
        rowStart={pickerContext?.rowStart ?? 1}
        colStart={pickerContext?.colStart ?? 1}
        scope="journal"
        initialCategory={pickerContext?.context === "topbar" ? "topbar" : undefined}
        isFreePlan={isFreePlan}
        onUpgradeClick={() => setShowPlanModal(true)}
        onPick={(widgetKey) => {
          if (!pickerContext) return;
          if (TOPBAR_WIDGET_KEYS.has(widgetKey)) {
            addWidget(widgetKey, 1, 0, 1, 1, "topbar");
          } else {
            const maxOccupiedRow = layout.slots
              .filter((s) => s.placement !== "topbar")
              .reduce((m, s) => Math.max(m, s.rowStart + s.rowSpan - 1), 0);
            addWidget(widgetKey, pickerContext.colStart, maxOccupiedRow + 1, pickerContext.maxColSpan);
          }
          trackWidgetAdded(widgetKey);
          setPickerContext(null);
        }}
      />

      <ResetLayoutDialog
        open={showResetDialog}
        onOpenChange={setShowResetDialog}
        onConfirm={async () => {
          const layoutId = (serverLayout as any)?.id as string | undefined;
          if (layoutId) {
            await resetLayoutFn({ layoutId });
            await refetchLayout();
          }
          setIsEditMode(false);
        }}
      />

      {/* Modifier / supprimer un trade */}
      {tradeEditing.dialogs}

      {/* Bulk delete trades confirm */}
      <Dialog open={isBulkDeleteConfirmOpen} onOpenChange={setIsBulkDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t.journal.confirmBulkDelete.replace("{count}", String(bulkDeleteIds.length))}
            </DialogTitle>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkDeleteConfirmOpen(false)}>{t.common.cancel}</Button>
            <Button variant="destructive" onClick={confirmBulkDelete}>{t.journal.deleteTrade}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Trade Modal — method chooser */}
      <AddTradeModal
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
        onSelectManual={() => {
          setIsCreateDialogOpen(true);
        }}
        onImportDone={() => refetchMetricsTrades()}
        onImportCSV={() => {
          setTimeout(() => setIsImportDialogOpen(true), 0);
        }}
      />

      {/* Create Dialog */}
      <ManualTradeDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onCreated={() => refetchMetricsTrades()}
        tradeCountForLimit={(metricsTradesData as any)?.total}
      />

      {/* Import Dialog */}
      <CsvImportDialog
        open={isImportDialogOpen}
        onOpenChange={setIsImportDialogOpen}
        onImported={() => refetchMetricsTrades()}
      />

      {/* Column Config Dialog */}
      <ColumnConfigDialog
        open={isColumnConfigDialogOpen}
        onOpenChange={setIsColumnConfigDialogOpen}
        onConfigChange={() => { refetchColumnPreferences(); }}
      />

      <PlanSelectionModal open={showPlanModal} onOpenChange={setShowPlanModal} />
    </PageTransition>
  );
}
